/**
 * 63.14 step 5 (ARCHITECTURE §5.22 "the walk, the cover map and the bots inside a built base"; DECISIONS 201 (10): "route
 * graph"): a modular building's route graph, from its grid for nothing. The ground floor's cells are joined where no wall
 * stands between them; the joined cells are **rooms** (a yard inside a wall line is a room; the cells round the building
 * and any cells open to them are the one **outside** room, 0); a door, a gate or a **breach** (a wall module destroyed,
 * 63.14 step 4 — its rubble is stepped over) is a **way** between the rooms on its two sides, a shut gate (StructureState.shut)
 * a way that blocks. A **next-hop table** (every room to every room, a breadth-first search per room when the graph is
 * made) answers a walker's next way in a lookup, so a bot pays nothing a tick for the search: the graph is keyed per
 * layout, shut set and module healths and memoised as the hit shape is (core/moduleShapes.ts), rebuilt on the tick a
 * leaf moves or a module goes. Stairs and ladders are noted (`stairs`, `ladders`) for step 8's upper floors; nothing
 * routes up them yet.
 *
 * `moduleRouteStep` is what the bots' walk asks (src/ai/actionBot.ts `go`): a walker and its goal in rooms apart are
 * walked way by way — in by a door, room to room, out by the far door — and a walker whose straight way crosses a wall
 * of a building it is not entering goes through its ways when that is shorter than round the wall's end. Behind
 * `GameConfig.modularBuildings`: off, no building has a layout and the walk is as it was, byte for byte. Pure.
 *
 * Later work, noted here, not built: a squad clearing a building room by room (the graph's rooms are the order), a squad
 * holding one (its ways are the posts, the cover map's points inside the rooms the slots), the gates opened and shut by
 * their holders (`shut` is the state; who writes it is a later step).
 */
import type { BuildingLayout, ModuleKind } from '../types/modules';
import type { GameState, StructureState, Vec3 } from '../types';
import { MODULE_CELL, MODULE_SPAN, MODULE_SHAPES, WALL_KINDS, OPENING_KINDS } from '../constants/modules';
import { ACTION_UNIT_RADIUS } from '../constants';
import { posesOf, layoutFor } from './modules';
import { shutOf } from './moduleShapes';
import { structureFrame } from './hits';
import { getSurfaceRadius } from './surface';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecScale, vecNormalize, vecSub, vecDot, vecLength } from '../math';
import { dacos, dcos, dsin } from './dmath';

const C = MODULE_CELL;
/** cos and sin of a quarter turn (exact). */
const COS = [1, 0, -1, 0] as const, SIN = [0, 1, 0, -1] as const;
const spanOf = (k: ModuleKind): number => MODULE_SPAN[k] ?? 1;

/** The outside: room 0. */
export const OUTSIDE = 0;
/** A cell nobody walks (a crate cell): no room. */
export const BLOCKED = -1;
/** How far past a way's line the walk is aimed (people): across the jambs and clear of them before the next leg. */
export const WAY_PAST_PEOPLE = 0.6;
/**
 * Item 48 (lane F, the door approach): how far in front of a way, on the walker's side, the walk is aimed first when its
 * straight line to past the way would meet the way's line off the opening (people) — a squad's members slid along the wall
 * to the jamb and stood there (2 of 3 with the local steer on, 1 of 3 off).
 */
export const WAY_FRONT_PEOPLE = 1.5;
/** A body's half-width (people): the radius the walk's discs give it (ACTION_UNIT_RADIUS at k 1, over the person's height; read at use — the constants barrel is mid-load when this module first runs). */
const bodyHalfPeople = (): number => ACTION_UNIT_RADIUS / PERSON_BODY_HEIGHT;
/** A way's opening half-width (people): to its nearest standing side part (the jambs, the posts, a container's face); a breach, or a kind with none, its whole span. */
const openingHalf = (w: RoomWay): number => {
  const span = (spanOf(w.kind) * C) / 2;
  if (w.breach) return span;
  let half = span;
  for (const p of MODULE_SHAPES[w.kind] ?? []) {
    if (p.leaf || p.pass || p.centre[1] - p.half[1] > 1 || Math.abs(p.centre[0]) <= p.half[0]) continue;   // (a leaf, a lintel overhead, a part across the middle)
    half = Math.min(half, Math.abs(p.centre[0]) - p.half[0]);
  }
  return half;
};
/**
 * The aim through way `w` crossing it the way `s` gives (+1 along its normal, −1 against) from (wx, wz): past it, as
 * wayAim — or, item 48, its front first when the straight line there would meet the way's line outside the lane a body
 * clears the opening's sides in (along the wall, the walk's discs then slide it to the jamb, where it stood).
 */
function throughWay(w: RoomWay, s: number, wx: number, wz: number): { x: number; z: number } {
  const past = { x: w.x + s * w.nx * WAY_PAST_PEOPLE, z: w.z + s * w.nz * WAY_PAST_PEOPLE };
  const depth = s * ((wx - w.x) * w.nx + (wz - w.z) * w.nz);   // (negative on the walker's side)
  if (depth >= 0) return past;
  const lateral = (wx - w.x) * w.nz - (wz - w.z) * w.nx;
  const crossing = lateral * (1 - -depth / (WAY_PAST_PEOPLE - depth));   // (where the straight line meets the way's line)
  if (Math.abs(crossing) <= Math.max(0, openingHalf(w) - bodyHalfPeople())) return past;
  return { x: w.x - s * w.nx * WAY_FRONT_PEOPLE, z: w.z - s * w.nz * WAY_FRONT_PEOPLE };
}
/** The kinds whose cell is not walked: the storage unit's four crates fill it. */
const BLOCKING_KINDS: ReadonlySet<ModuleKind> = new Set<ModuleKind>(['crate']);

/** A way between two rooms: the module (a door, a gate, a breach), the rooms either side, its point and its normal (people, the building's frame; the normal from `a` toward `b`). */
export interface RoomWay {
  k: number;
  kind: ModuleKind;
  a: number;
  b: number;
  x: number;
  z: number;
  nx: number;
  nz: number;
  /** A destroyed wall module: open by its going (its rubble, if any, is stepped over). */
  breach: boolean;
  /** A gate shut (StructureState.shut): the way blocks until it opens. */
  shut: boolean;
}

export interface RoomGraph {
  layout: BuildingLayout;
  /** The cell grid the rooms are laid on: from cell (i0, j0), `cols` × `rows` (the layout's extent and one ring of cells round it, the outside). */
  i0: number;
  j0: number;
  cols: number;
  rows: number;
  /** Each cell's room (OUTSIDE 0, BLOCKED −1), row-major: `roomOf[(j − j0) * cols + (i − i0)]`. */
  roomOf: Int16Array;
  /** How many rooms, the outside counted. */
  rooms: number;
  ways: readonly RoomWay[];
  /** `hops[from * rooms + to]`: how many ways from room to room over the open ways (0 the same room, −1 none). */
  hops: Int16Array;
  /** For step 8: the stairs and the ladders, by module and cell (the floor they rise from). */
  stairs: ReadonlyArray<{ k: number; i: number; j: number; floor: number }>;
  ladders: ReadonlyArray<{ k: number; i: number; j: number; floor: number }>;
  /** The modules across the cells' edges (`edgeX[c]` between cell c and the cell south of it, `edgeZ[c]` the cell west): 0 none, 1 a way, 2 solid. */
  edgeX: Uint8Array;
  edgeZ: Uint8Array;
  /** A container room's cells: the module that owns them (−1 elsewhere); its sides are solid but for its door. */
  owner: Int16Array;
}

const memo = new WeakMap<BuildingLayout, Map<string, RoomGraph>>();
const wornMemo = new WeakMap<readonly number[], Map<string, RoomGraph>>();

/**
 * The layout's room graph with these modules shut and (step 4) these module healths — memoised per layout, or per
 * `modules` array (a new one each blow), by shut set: the same object while nothing changes.
 */
export function roomGraph(layout: BuildingLayout, shut: ReadonlySet<number>, modules?: readonly number[]): RoomGraph {
  const key = [...shut].sort((a, b) => a - b).join(',');
  let byShut = modules ? wornMemo.get(modules) : memo.get(layout);
  if (!byShut) { byShut = new Map(); if (modules) wornMemo.set(modules, byShut); else memo.set(layout, byShut); }
  const hit = byShut.get(key);
  if (hit) return hit;
  const g = buildRoomGraph(layout, shut, modules);
  byShut.set(key, g);
  return g;
}

function buildRoomGraph(layout: BuildingLayout, shut: ReadonlySet<number>, modules?: readonly number[]): RoomGraph {
  // the extent: the layout's box and everything it places on the ground (wire a cell out), plus a ring of cells — the outside
  let minI = 0, maxI = layout.cols, minJ = 0, maxJ = layout.rows;
  for (const m of layout.modules) {
    if ((m.floor ?? 0) !== 0) continue;
    const span = spanOf(m.kind);
    const i1 = m.at === 'edgeX' ? m.i + span : m.at === 'cell' ? m.i + (m.turn === 1 || m.turn === 3 ? 1 : span) : m.i;
    const j1 = m.at === 'edgeZ' ? m.j + span : m.at === 'cell' ? m.j + (m.turn === 1 || m.turn === 3 ? span : 1) : m.j;
    minI = Math.min(minI, m.i); maxI = Math.max(maxI, i1); minJ = Math.min(minJ, m.j); maxJ = Math.max(maxJ, j1);
  }
  const i0 = minI - 1, j0 = minJ - 1, cols = maxI - minI + 2, rows = maxJ - minJ + 2, n = cols * rows;
  const at = (i: number, j: number) => (j - j0) * cols + (i - i0);
  const inGrid = (i: number, j: number) => i >= i0 && i < i0 + cols && j >= j0 && j < j0 + rows;
  const gone = (k: number) => !!modules && modules[k] <= 0;
  // blocked cells, and the modules across edges: `edgeX[at(i, j)]` the edge between cells (i, j − 1) and (i, j), `edgeZ` between
  // (i − 1, j) and (i, j) — 1 a way (a door, an open gate, a breach: rooms apart, the walk through), 2 solid (a wall, a shut gate)
  const blocked = new Uint8Array(n), edgeX = new Uint8Array(n), edgeZ = new Uint8Array(n);
  const ways: RoomWay[] = [];
  const stairs: Array<{ k: number; i: number; j: number; floor: number }> = [], ladders: typeof stairs = [];
  const poses = posesOf(layout);
  const owner = new Int16Array(n).fill(-1);   // a container room's cells: the module that owns them (a room of their own, entered by its door)
  layout.modules.forEach((m, k) => {
    const floor = m.floor ?? 0;
    if (m.kind === 'stairs') { stairs.push({ k, i: m.i, j: m.j, floor }); return; }
    if (m.kind === 'ladder') { ladders.push({ k, i: m.i, j: m.j, floor }); return; }
    if (floor !== 0) return;
    if (gone(k) && !WALL_KINDS.has(m.kind)) return;   // (a destroyed crate or container: its cell is free)
    if (BLOCKING_KINDS.has(m.kind) && m.at === 'cell' && inGrid(m.i, m.j)) { blocked[at(m.i, m.j)] = 1; return; }
    if (m.kind === 'containerRoom' && m.at === 'cell') {
      // its two cells along its run (turn 0 or 2 along x, 1 or 3 along z), walled on every side but its door's
      const turn = m.turn ?? 0, alongX = turn % 2 === 0;
      const cells = [[m.i, m.j], alongX ? [m.i + 1, m.j] : [m.i, m.j + 1]] as const;
      for (const [i, j] of cells) if (inGrid(i, j)) owner[at(i, j)] = k;
      return;
    }
    if (!WALL_KINDS.has(m.kind)) return;
    const span = spanOf(m.kind), opening = OPENING_KINDS.has(m.kind), breach = gone(k);
    const isShut = opening && !breach && m.kind !== 'wallDoor' && shut.has(k);
    for (let s = 0; s < span; s++) {
      const i = m.at === 'edgeX' ? m.i + s : m.i, j = m.at === 'edgeZ' ? m.j + s : m.j;
      if (!inGrid(i, j)) continue;
      (m.at === 'edgeX' ? edgeX : edgeZ)[at(i, j)] = (opening && !isShut) || breach ? 1 : 2;
    }
    if (opening || breach) {
      const p = poses[k];
      const alongX = m.at === 'edgeX';
      ways.push({ k, kind: m.kind, a: -1, b: -1, x: p.x, z: p.z, nx: alongX ? 0 : 1, nz: alongX ? 1 : 0, breach, shut: isShut });
    }
  });
  // the container rooms: each a room of its own; its door a way to the cell before its −z face (turned)
  const containers: Array<{ k: number; x: number; z: number; nx: number; nz: number; outCell: number }> = [];
  layout.modules.forEach((m, k) => {
    if (m.kind !== 'containerRoom' || (m.floor ?? 0) !== 0 || gone(k)) return;
    const turn = m.turn ?? 0, c = COS[turn], s = SIN[turn], p = poses[k];
    // the door's centre on the −z face of the module's frame, local (0, −1.2), in the building's frame (x' = x c + z s, z' = −x s + z c),
    // and its normal out of the container (local −z): (−s, −c)
    const wx = p.x - 1.2 * s, wz = p.z - 1.2 * c;
    const ox = p.x - 1.6 * s, oz = p.z - 1.6 * c;   // a step further out: the cell the door opens to
    const oi = Math.floor((ox + (layout.cols * C) / 2) / C), oj = Math.floor((oz + (layout.rows * C) / 2) / C);
    containers.push({ k, x: wx, z: wz, nx: -s, nz: -c, outCell: inGrid(oi, oj) ? at(oi, oj) : -1 });
  });
  // the rooms: union the cells across every edge no wall stands on (a container's cells only with each other)
  const parent = new Int32Array(n);
  for (let c = 0; c < n; c++) parent[c] = c;
  const find = (c: number): number => { while (parent[c] !== c) { parent[c] = parent[parent[c]]; c = parent[c]; } return c; };
  const union = (a: number, b: number) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[rb] = ra; };
  for (let j = j0; j < j0 + rows; j++) for (let i = i0; i < i0 + cols; i++) {
    const c = at(i, j);
    if (blocked[c]) continue;
    if (i > i0 && !blocked[c - 1] && !edgeZ[c] && owner[c] === owner[c - 1]) union(c, c - 1);
    if (j > j0 && !blocked[c - cols] && !edgeX[c] && owner[c] === owner[c - cols]) union(c, c - cols);
  }
  // the ring is the outside, whatever walls stand on it
  const ring = at(i0, j0);
  for (let i = i0; i < i0 + cols; i++) { union(ring, at(i, j0)); union(ring, at(i, j0 + rows - 1)); }
  for (let j = j0; j < j0 + rows; j++) { union(ring, at(i0, j)); union(ring, at(i0 + cols - 1, j)); }
  const roomOf = new Int16Array(n), roomId = new Map<number, number>();
  roomId.set(find(ring), OUTSIDE);
  for (let c = 0; c < n; c++) {
    if (blocked[c]) { roomOf[c] = BLOCKED; continue; }
    const r = find(c);
    let id = roomId.get(r);
    if (id === undefined) { id = roomId.size; roomId.set(r, id); }
    roomOf[c] = id;
  }
  const rooms = roomId.size;
  // the ways' rooms: a wall way joins the cells either side of its edge; a container's door its cells and the cell before it
  const kept: RoomWay[] = [];
  for (const w of ways) {
    const m = layout.modules[w.k];
    const alongX = m.at === 'edgeX';
    const i = m.i + (alongX ? Math.floor(spanOf(m.kind) / 2) : 0), j = m.j + (alongX ? 0 : Math.floor(spanOf(m.kind) / 2));   // (a spanning way's middle cell)
    const ca = alongX ? [i, j - 1] : [i - 1, j], cb = [i, j];
    const ra = inGrid(ca[0], ca[1]) ? roomOf[at(ca[0], ca[1])] : OUTSIDE, rb = inGrid(cb[0], cb[1]) ? roomOf[at(cb[0], cb[1])] : OUTSIDE;
    if (ra === BLOCKED || rb === BLOCKED) continue;
    kept.push({ ...w, a: ra, b: rb });
  }
  for (const c of containers) {
    const m = layout.modules[c.k], inside = inGrid(m.i, m.j) ? roomOf[at(m.i, m.j)] : BLOCKED;
    const out = c.outCell >= 0 ? roomOf[c.outCell] : OUTSIDE;
    if (inside === BLOCKED || out === BLOCKED) continue;
    kept.push({ k: c.k, kind: 'containerRoom', a: inside, b: out, x: c.x, z: c.z, nx: c.nx, nz: c.nz, breach: false, shut: false });
  }
  // the hop table: a breadth-first search from every room over the open ways (the way taken is chosen at the query, by distance, among the ways that keep to the shortest)
  const hops = new Int16Array(rooms * rooms).fill(-1);
  const adj: number[][] = Array.from({ length: rooms }, () => []);
  kept.forEach((w, wi) => { if (w.shut || w.a === w.b) return; adj[w.a].push(wi); adj[w.b].push(wi); });
  for (let from = 0; from < rooms; from++) {
    hops[from * rooms + from] = 0;
    const queue: number[] = [from];
    for (let q = 0; q < queue.length; q++) {
      const r = queue[q], h = hops[from * rooms + r];
      for (const wi of adj[r]) {
        const w = kept[wi], other = w.a === r ? w.b : w.a;
        if (hops[from * rooms + other] >= 0) continue;
        hops[from * rooms + other] = h + 1;
        queue.push(other);
      }
    }
  }
  return { layout, i0, j0, cols, rows, roomOf, rooms, ways: kept, hops, stairs, ladders, edgeX, edgeZ, owner };
}

/** The room at a point of the building's frame (people): the outside beyond the grid; a blocked cell takes the room of a cell beside it (none: BLOCKED). */
export function roomAt(g: RoomGraph, x: number, z: number): number {
  const i = Math.floor((x + (g.layout.cols * C) / 2) / C), j = Math.floor((z + (g.layout.rows * C) / 2) / C);
  if (i < g.i0 || i >= g.i0 + g.cols || j < g.j0 || j >= g.j0 + g.rows) return OUTSIDE;
  const r = g.roomOf[(j - g.j0) * g.cols + (i - g.i0)];
  if (r !== BLOCKED) return r;
  // the nearest cell beside it that is walked (by the point's place in the cell; a tie south, east, west, north)
  const fx = (x + (g.layout.cols * C) / 2) / C - i - 0.5, fz = (z + (g.layout.rows * C) / 2) / C - j - 0.5;
  const sides: Array<[number, number, number]> = [[0, -1, fz], [1, 0, -fx], [-1, 0, fx], [0, 1, -fz]];
  sides.sort((a, b) => a[2] - b[2]);
  for (const [di, dj] of sides) {
    const ii = i + di, jj = j + dj;
    if (ii < g.i0 || ii >= g.i0 + g.cols || jj < g.j0 || jj >= g.j0 + g.rows) return OUTSIDE;
    const rr = g.roomOf[(jj - g.j0) * g.cols + (ii - g.i0)];
    if (rr !== BLOCKED) return rr;
  }
  return BLOCKED;
}

/**
 * The ways a walker passes from room `from` to room `to`, in order (null: no open way; empty: the same room): the fewest
 * ways, and among those, from `at` toward `goal` (people) the nearest at each room — the north door for a goal to the
 * north. Without the points, the first way by index.
 */
export function roomPath(g: RoomGraph, from: number, to: number, at?: { x: number; z: number }, goal?: { x: number; z: number }): RoomWay[] | null {
  if (from < 0 || to < 0 || g.hops[from * g.rooms + to] < 0) return null;
  const out: RoomWay[] = [];
  let r = from, px = at?.x ?? 0, pz = at?.z ?? 0;
  for (let guard = 0; guard < g.rooms && r !== to; guard++) {
    const left = g.hops[r * g.rooms + to];
    let best: RoomWay | null = null, bestCost = Infinity;
    for (const w of g.ways) {
      if (w.shut || w.a === w.b || (w.a !== r && w.b !== r)) continue;
      const other = w.a === r ? w.b : w.a;
      if (g.hops[other * g.rooms + to] !== left - 1) continue;
      const cost = at && goal ? Math.sqrt((w.x - px) * (w.x - px) + (w.z - pz) * (w.z - pz)) + Math.sqrt((goal.x - w.x) * (goal.x - w.x) + (goal.z - w.z) * (goal.z - w.z)) : 0;
      // 69.90: the last way's leg to the goal walled (the goal beyond the building, the near door on its wrong side) ranks after a
      // clear one — through it, the same-room detour (roomStep) would send the walker straight back in, a way a tick
      const walled = !!goal && other === to && (() => { const p = wayAim(w, r); return segmentWalled(g, p.x, p.z, goal.x, goal.z); })();
      const ranked = cost + (walled ? 1e9 : 0);
      if (ranked < bestCost) { best = w; bestCost = ranked; }
    }
    if (!best) return null;
    out.push(best);
    r = best.a === r ? best.b : best.a;
    px = best.x; pz = best.z;
  }
  return r === to ? out : null;
}

/** The point the walk aims at to pass way `w` from room `from` (people): its centre, WAY_PAST_PEOPLE past its line on the far side. */
export function wayAim(w: RoomWay, from: number): { x: number; z: number } {
  const s = w.a === from ? 1 : -1;
  return { x: w.x + s * w.nx * WAY_PAST_PEOPLE, z: w.z + s * w.nz * WAY_PAST_PEOPLE };
}

/** Whether the straight segment (x0, z0) → (x1, z1) (people) crosses something solid: a wall or a shut gate on an edge it passes, a container's side, a blocked cell. A way (a door, an open gate, a breach) is passed. */
export function segmentWalled(g: RoomGraph, x0: number, z0: number, x1: number, z1: number): boolean {
  // the cells the segment passes, by stepping the nearer of the next x and z cell lines
  const ox = (g.layout.cols * C) / 2, oz = (g.layout.rows * C) / 2;
  let i = Math.floor((x0 + ox) / C), j = Math.floor((z0 + oz) / C);
  const i1 = Math.floor((x1 + ox) / C), j1 = Math.floor((z1 + oz) / C);
  const dx = x1 - x0, dz = z1 - z0;
  const si = dx > 0 ? 1 : -1, sj = dz > 0 ? 1 : -1;
  const inGrid = (ii: number, jj: number) => ii >= g.i0 && ii < g.i0 + g.cols && jj >= g.j0 && jj < g.j0 + g.rows;
  const cellOf = (ii: number, jj: number) => (jj - g.j0) * g.cols + (ii - g.i0);
  for (let guard = 0; guard < 4 * (g.cols + g.rows) && (i !== i1 || j !== j1); guard++) {
    // the parameter at which the segment leaves this cell across x and across z
    const nxLine = (i + (si > 0 ? 1 : 0)) * C - ox, nzLine = (j + (sj > 0 ? 1 : 0)) * C - oz;
    const tx = dx !== 0 ? (nxLine - x0) / dx : Infinity, tz = dz !== 0 ? (nzLine - z0) / dz : Infinity;
    const pi = i, pj = j;
    if (tx < tz) i += si; else j += sj;
    if (!inGrid(i, j) || !inGrid(pi, pj)) continue;   // (beyond the grid: the open outside)
    const c = cellOf(i, j), pc = cellOf(pi, pj);
    if (g.roomOf[c] === BLOCKED) return true;
    // the edge crossed: edgeZ at the eastern of the two cells across x, edgeX at the northern across z
    const e = tx < tz ? g.edgeZ[si > 0 ? c : pc] : g.edgeX[sj > 0 ? c : pc];
    if (e === 2) return true;
    if (g.owner[c] !== g.owner[pc] && e === 0) return true;   // a container's side (its door is the way on the edge)
  }
  return false;
}

/** The length of a polyline through the frame's points (people). */
function pathLength(points: ReadonlyArray<{ x: number; z: number }>): number {
  let l = 0;
  for (let i = 1; i < points.length; i++) { const dx = points[i].x - points[i - 1].x, dz = points[i].z - points[i - 1].z; l += Math.sqrt(dx * dx + dz * dz); }
  return l;
}

/**
 * The next point to walk to (people, the building's frame), for a walker at (wx, wz) going to (gx, gz): rooms apart,
 * the first way of the next-hop path (aimed past its line); the same room with a wall across the straight way, the
 * ways through that make the shortest path with at most two ways (out by one door and in — or out — by another) when
 * that is shorter than the detour round the wall's end (twice the distance from the crossing to the nearer end of the
 * layout's extent along the wall, added to the straight way); else null: the walk goes straight and the steer rounds
 * what it meets.
 */
export function roomStep(g: RoomGraph, wx: number, wz: number, gx: number, gz: number): { x: number; z: number; way: RoomWay; path: RoomWay[] } | null {
  const from = roomAt(g, wx, wz), to = roomAt(g, gx, gz);
  if (from === BLOCKED || to === BLOCKED) return null;
  if (from !== to) {
    const path = roomPath(g, from, to, { x: wx, z: wz }, { x: gx, z: gz });
    if (!path || !path.length) return null;
    const aim = throughWay(path[0], path[0].a === from ? 1 : -1, wx, wz);   // (wayAim's past, or its front first: item 48)
    return { ...aim, way: path[0], path };
  }
  if (!segmentWalled(g, wx, wz, gx, gz)) return null;
  const straight = Math.sqrt((gx - wx) * (gx - wx) + (gz - wz) * (gz - wz));
  // the detour round the wall's end: to the nearer end of the extent along the wall the straight way crosses first, and back
  const hx = (g.cols * C) / 2, hz = (g.rows * C) / 2;   // (the grid's half extents round the layout's centre — the ring in)
  const cx = (g.i0 + g.cols / 2) * C - (g.layout.cols * C) / 2, cz = (g.j0 + g.rows / 2) * C - (g.layout.rows * C) / 2;
  const endX = Math.min(Math.abs(wx - (cx - hx)), Math.abs(cx + hx - wx)), endZ = Math.min(Math.abs(wz - (cz - hz)), Math.abs(cz + hz - wz));
  const detour = straight + 2 * Math.min(endX, endZ);
  // the ways out of this room and the two-way paths back into it
  let best: { length: number; path: RoomWay[] } | null = null;
  const consider = (path: RoomWay[]) => {
    const pts = [{ x: wx, z: wz }, ...path.map(w => ({ x: w.x, z: w.z })), { x: gx, z: gz }];
    const length = pathLength(pts);
    if (length < detour && (!best || length < best.length)) best = { length, path };
  };
  for (const w of g.ways) {
    if (w.shut) continue;
    if (w.a === from && w.b === from) consider([w]);
    else if (w.a === from || w.b === from) {
      const other = w.a === from ? w.b : w.a;
      for (const v of g.ways) if (v !== w && !v.shut && ((v.a === other && v.b === from) || (v.b === other && v.a === from))) consider([w, v]);
    }
  }
  if (!best) return null;
  const chosen = (best as { length: number; path: RoomWay[] }).path;
  const w0 = chosen[0];
  // (a same-room way: aimed past it toward the goal's side of its line)
  const side = w0.a === w0.b ? ((gx - w0.x) * w0.nx + (gz - w0.z) * w0.nz >= 0 ? 1 : -1) : (w0.a === from ? 1 : -1);
  return { ...throughWay(w0, side, wx, wz), way: w0, path: chosen };
}

// ---- the buildings on a planet, and the world-space query the bots make ------------------------------------------------------

/** A modular building's frame on its planet: the structure, its layout, its base and axes, the person (world units) and its reach (world). */
export interface BuildingFrame { s: StructureState; layout: BuildingLayout; base: Vec3; up: Vec3; tx: Vec3; tz: Vec3; person: number; reach: number; /** the base's direction's point at the planet's radius (world): the far test's centre */ centre: Vec3 }

const frameMemo = new WeakMap<StructureState, BuildingFrame | null>();
const listMemo = new WeakMap<readonly StructureState[], Map<number, readonly BuildingFrame[]>>();

/** The standing modular buildings on a planet, framed; memoised per structures array and per structure. Empty with the switch off. */
export function buildingFrames(state: Pick<GameState, 'structures' | 'planets' | 'config'>, planetIndex: number): readonly BuildingFrame[] {
  let byPlanet = listMemo.get(state.structures);
  if (!byPlanet) { byPlanet = new Map(); listMemo.set(state.structures, byPlanet); }
  const hit = byPlanet.get(planetIndex);
  if (hit) return hit;
  const out: BuildingFrame[] = [];
  const planet = state.planets[planetIndex];
  if (planet) for (const s of state.structures) {
    if (s.planetIndex !== planetIndex || s.health <= 0) continue;
    let f = frameMemo.get(s);
    if (f === undefined) {
      const layout = layoutFor(s, state.config);
      if (!layout) f = null;
      else {
        const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
        const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);
        const base = vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + (s.baseLift ?? 0)));
        const cols = layout.cols + 2, rows = layout.rows + 2;   // (the extent and its ring, roughly: the grid's reach, for the near test)
        f = { s, layout, base, up, tx, tz, person, reach: Math.sqrt(cols * cols + rows * rows) * (C / 2) * person * 1.5, centre: vecAdd(planet.position, vecScale(up, planet.radius)) };
      }
      frameMemo.set(s, f);
    }
    if (f) out.push(f);
  }
  byPlanet.set(planetIndex, out);
  return out;
}

/**
 * A direction's point on the ground in a building's frame (people) — taken at the base's own radius, as moduleRouteStep's
 * answer is given back (the base plus the frame's axes): 69.14, at the planet's radius it was not the inverse where the ground
 * stands off it (a tenth higher on the pool's board) — every aim came back short by that share, and a walker leaving the motor
 * pool by its gate was sent to a point a person inside it, reached it, and stood there.
 */
function localOf(f: BuildingFrame, planet: { position: Vec3; radius: number }, dir: Vec3): { x: number; z: number } {
  const p = vecSub(vecAdd(planet.position, vecScale(dir, vecLength(vecSub(f.base, planet.position)))), f.base);
  return { x: vecDot(p, f.tx) / f.person, z: vecDot(p, f.tz) / f.person };
}

/**
 * DECISIONS 300: whether `goal` lies in a room of a modular building on the planet (not outside it, not in a wall) — a goal
 * the room graph walks to, which the local steer (core/steer.ts reachableGoal) leaves where it is rather than moving it
 * out of the building's footprint.
 */
export function inBuildingRoom(state: Pick<GameState, 'structures' | 'planets' | 'config'>, planetIndex: number, goal: Vec3): boolean {
  return buildingRoomAt(state, planetIndex, goal) !== null;
}

/**
 * The room of a modular building `dir` stands in on the planet — its structure's id and the room's number — or null (outside
 * every building, or in a wall). Two points share a room when both answers match (ai/actionPositions.ts: a squad sent into a
 * room keeps its posts in it).
 */
export function buildingRoomAt(state: Pick<GameState, 'structures' | 'planets' | 'config'>, planetIndex: number, dir: Vec3): { id: number; room: number } | null {
  const planet = state.planets[planetIndex];
  for (const f of buildingFrames(state, planetIndex)) {
    const g = localOf(f, planet, dir), reach = f.reach / f.person;
    if (g.x * g.x + g.z * g.z >= reach * reach) continue;
    const r = roomAt(roomGraph(f.layout, shutOf(f.s, f.layout), f.s.modules), g.x, g.z);
    if (r !== OUTSIDE && r !== BLOCKED) return { id: f.s.id, room: r };
  }
  return null;
}

/** The centres of a room's cells (as buildingRoomAt names it), as directions from the planet's centre: places to stand in it. */
export function buildingRoomCells(state: Pick<GameState, 'structures' | 'planets' | 'config'>, planetIndex: number, at: { id: number; room: number }): Vec3[] {
  const planet = state.planets[planetIndex];
  const f = buildingFrames(state, planetIndex).find(x => x.s.id === at.id);
  if (!f) return [];
  const g = roomGraph(f.layout, shutOf(f.s, f.layout), f.s.modules), out: Vec3[] = [];
  for (let j = 0; j < g.rows; j++) for (let i = 0; i < g.cols; i++) {
    if (g.roomOf[j * g.cols + i] !== at.room) continue;
    const x = (g.i0 + i + 0.5) * C - (g.layout.cols * C) / 2, z = (g.j0 + j + 0.5) * C - (g.layout.rows * C) / 2;
    out.push(vecNormalize(vecSub(vecAdd(f.base, vecAdd(vecScale(f.tx, x * f.person), vecScale(f.tz, z * f.person))), planet.position)));
  }
  return out;
}

/**
 * The walk's question (src/ai/actionBot.ts `go`): for a walker at `walker` (a direction from the planet's centre) going
 * to `goal`, the next point to walk to by the route graph of a modular building either is in or the straight way
 * crosses — a direction from the planet's centre — or null when no building is involved (the walk as it was). The
 * walker's own building first (its way out), then the goal's. Each building's graph is keyed on its shut set and module
 * healths (a breach opens a way the tick its wall goes).
 */
export function moduleRouteStep(state: Pick<GameState, 'structures' | 'planets' | 'config'>, planetIndex: number, walker: Vec3, goal: Vec3): Vec3 | null {
  const frames = buildingFrames(state, planetIndex);
  if (!frames.length) return null;
  const planet = state.planets[planetIndex];
  // the walker's point (world)
  const R = planet.radius, px = planet.position.x, py = planet.position.y, pz = planet.position.z;
  const wl = 1 / Math.sqrt(walker.x * walker.x + walker.y * walker.y + walker.z * walker.z), gl = 1 / Math.sqrt(goal.x * goal.x + goal.y * goal.y + goal.z * goal.z);
  const nx = walker.x * wl, ny = walker.y * wl, nz = walker.z * wl;
  const wx = px + nx * R, wy = py + ny * R, wz = pz + nz * R;
  // 69.90: the walk's angle to the goal, and its heading there (the great circle's tangent at the walker)
  const cosA = Math.max(-1, Math.min(1, nx * goal.x * gl + ny * goal.y * gl + nz * goal.z * gl)), arc = dacos(cosA);
  const hx = goal.x * gl - nx * cosA, hy = goal.y * gl - ny * cosA, hz = goal.z * gl - nz * cosA, hl = Math.sqrt(hx * hx + hy * hy + hz * hz);
  for (const f of frames) {
    // 69.90: the goal cut to its walk's first two reaches of this building (a point on the great circle the walk follows): a
    // goal far round the planet folds in the building's flat frame — its straight way there is not the walk's, and buildings
    // the walk never passes claimed it (story-6: a route and the straight walk opposite, a way a tick). A goal in its rooms is
    // within two reaches of a walker the building can route, so it is never cut
    const cut = (2 * f.reach) / R, cutting = arc > cut && hl > 1e-9;
    const goalF = cutting ? { x: nx * dcos(cut) + (hx / hl) * dsin(cut), y: ny * dcos(cut) + (hy / hl) * dsin(cut), z: nz * dcos(cut) + (hz / hl) * dsin(cut) } : goal;
    // the leg's length: a building farther from the walker than its reach and the leg is not reached (a far test in scalars: a bot pays a few dot products a building it is nowhere near)
    const leg = R * Math.min(arc, cut);   // (the arc: never shorter than its chord)
    const far = f.reach + leg, cx = f.centre.x - wx, cy = f.centre.y - wy, cz = f.centre.z - wz;
    if (cx * cx + cy * cy + cz * cz > far * far) continue;
    // the near test: the walker or the goal within the building's reach, or the straight way passing within it
    const w = localOf(f, planet, walker), g = localOf(f, planet, goalF);
    const reach = f.reach / f.person;
    const wIn = w.x * w.x + w.z * w.z < reach * reach, gIn = g.x * g.x + g.z * g.z < reach * reach;
    if (!wIn && !gIn) {
      // the segment's nearest point to the origin
      const dx = g.x - w.x, dz = g.z - w.z, l2 = dx * dx + dz * dz;
      if (l2 < 1e-12) continue;
      const t = Math.max(0, Math.min(1, -(w.x * dx + w.z * dz) / l2));
      const px = w.x + dx * t, pz = w.z + dz * t;
      if (px * px + pz * pz >= reach * reach) continue;
    }
    const graph = roomGraph(f.layout, shutOf(f.s, f.layout), f.s.modules);
    const step = roomStep(graph, w.x, w.z, g.x, g.z);
    if (!step) continue;
    return vecNormalize(vecSub(vecAdd(f.base, vecAdd(vecScale(f.tx, step.x * f.person), vecScale(f.tz, step.z * f.person))), planet.position));
  }
  return null;
}
